import { forwardRef, type ChangeEvent, type InputHTMLAttributes } from "react";

type MaskFn = (raw: string) => string;

export interface MaskedInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  mask: MaskFn;
  /**
   * Handler padrão. Recebe o ChangeEvent já com o valor pós-mask no
   * `target.value`. Use para integração com `react-hook-form` Controller
   * via `field.onChange`.
   */
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Atalho idiomático: recebe direto o valor mascarado (string). */
  onValueChange?: (masked: string) => void;
}

/**
 * Input controlado que aplica uma máscara pura-JS no onChange.
 *
 * Contrato: é controlled-only. O consumer é responsável por passar `value`
 * (normalmente vindo de RHF `field.value` ou state local) e reenviar o
 * valor mascarado via `onChange`/`onValueChange`.
 */
export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  function MaskedInput({ mask, onChange, onValueChange, ...rest }, ref) {
    return (
      <input
        ref={ref}
        {...rest}
        onChange={(event) => {
          const masked = mask(event.target.value);
          event.target.value = masked;
          onChange?.(event);
          onValueChange?.(masked);
        }}
      />
    );
  }
);
